import {isNull} from 'lodash';
import {connect} from 'react-redux';

import {ScrollDirectionsEnum} from '../../../../../srcCommon/scroll/scrollerTypes';
import {
  AppParametricSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../helpers/reselectHelpers';
import {AppState} from '../../../../store/appState';
import {AsyncMoreStatusesEnum} from '../../../../store/appTypes';
import {
  FetchCellsCachePoliciesEnum,
  fetchMoreConversationsAsyncAction,
  navigateCellsAsyncAction,
} from '../../../../store/panels/cells/cellsPanelActions';
import {CellsPanelConversationsPageState} from '../../../../store/panels/cells/cellsPanelState';
import {
  makeGetConversationSelection,
  makeGetKnownSelectedConversations,
  makeGetSelectedLastManualMessages,
} from '../../../../store/panels/cells/selectors/cellsPanelSelectionSelectors';
import {CellsPanelId} from '../../../../store/panels/panelStateBase';
import {makeGetSetupActionsVariant} from '../../viewer/empty/emptyInboxesContainer';
import {CellsConversations} from './cellsConversations';

/*
 * Props.
 */

interface CellsConversationsContainerProps {
  panelId: CellsPanelId;
  page: CellsPanelConversationsPageState;
  cachePolicy: FetchCellsCachePoliciesEnum;
}

/*
 * Selectors.
 */

function getCellsPanelId(_state: AppState, props: CellsConversationsContainerProps) {
  return props.panelId;
}

function getPageParameters(_state: AppState, props: CellsConversationsContainerProps) {
  return props.page.parameters;
}

function makeGetHasCtaVariant(): AppParametricSelector<CellsConversationsContainerProps, boolean> {
  const getCtaVariant = makeGetSetupActionsVariant(getPageParameters);

  return (state, props) => !isNull(getCtaVariant(state, props));
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CellsConversationsContainerProps>()(() => {
  const getConversationSelection = makeGetConversationSelection(getPageParameters, getCellsPanelId);
  const getKnownSelectedConversations = makeGetKnownSelectedConversations(
    getConversationSelection,
    getCellsPanelId,
  );
  const getSelectedLastManualMessages = makeGetSelectedLastManualMessages(getKnownSelectedConversations);

  return {
    knownSelectedConversations: getKnownSelectedConversations,
    selectedDragMessages: getSelectedLastManualMessages,
    hasCtaVariant: makeGetHasCtaVariant(),
  };
});

export const mapDispatchToProps = makeMapDispatchToProps<CellsConversationsContainerProps>()(
  ({panelId, page, cachePolicy}) => ({
    onRetryClick: () => navigateCellsAsyncAction(panelId, page.parameters),
    onRequestMoreAsyncInner: (direction, isUsingSearchRawEndpoint) => {
      const {moreStatusTop, moreStatusBottom, topBoundary, bottomBoundary} = page;

      // Check if we're allowed to load more in that direction.
      const isTop = direction === ScrollDirectionsEnum.TOP;
      const moreStatus = isTop ? moreStatusTop : moreStatusBottom;
      if (moreStatus !== AsyncMoreStatusesEnum.HAS_MORE) {
        throw new Error('Nothing to load.');
      }

      // Find the boundary that we'll use as reference for the next batch.
      const referenceBoundary = isTop ? topBoundary : bottomBoundary;
      if (!referenceBoundary) {
        throw new Error('A reference boundary could not be found.');
      }

      return fetchMoreConversationsAsyncAction(
        panelId,
        page.uid,
        page.parameters,
        direction,
        referenceBoundary,
        cachePolicy,
        isUsingSearchRawEndpoint,
      );
    },
  }),
);

export const CellsConversationsContainer = connect(mapStateToProps, mapDispatchToProps)(CellsConversations);
